import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { Linking, StyleProp, TextStyle, View, ViewStyle } from 'react-native'
import { ButtonProps } from 'react-native-paper'
import { StyleSheet } from 'react-native-unistyles'
import { z } from 'zod'

import FormFieldText from '@/components/form/FormFieldText'
import UserAvatar from '@/components/navigation/UserAvatar'
import NotFound from '@/components/shared/NotFound'
import UniButton from '@/components/unistyles/UniButton'
import UniText from '@/components/unistyles/UniText'
import UserService from '@/services/userService'

interface Props {
  buttonStyle?: StyleProp<ViewStyle>
  buttonMode?: ButtonProps['mode']
  formContainerStyle: StyleProp<ViewStyle>
  titleStyle: StyleProp<TextStyle>
}

const UserProfile = ({
  buttonStyle,
  buttonMode,
  formContainerStyle,
  titleStyle,
}: Props) => {
  const { user, isLoading, currentOrganization } = UserService.useCurrentUser()

  const updateMutation = UserService.useUpdateMutation()

  const defaultValues = { ...user }

  const form = useForm<FormData>({
    // mode: 'onBlur',
    resolver: zodResolver(formSchema),
    defaultValues,
  })

  const {
    control,
    formState: { errors, isDirty, isSubmitting },
    handleSubmit,
    reset,
  } = form

  useEffect(() => {
    reset(user)
  }, [reset, user])

  const onSubmit = async (data: FormData) => {
    if (!currentOrganization?.id) {
      return
    }

    updateMutation.mutate(data, {
      onSuccess: (updatedData) => {
        reset({ ...updatedData })
      },
    })
  }

  if (!isLoading && !user) return <NotFound />

  return (
    <View>
      <UniText style={titleStyle} variant="titleLarge">
        Basic Profile
      </UniText>
      <View>
        <View style={styles.avatarContainer}>
          <UserAvatar size={100} user={user} />
          <View style={styles.avatarTextContainer}>
            <UniText style={styles.gravatarText} variant="bodySmall">
              Profile pictures are powered by Gravatar
            </UniText>
            <UniButton
              mode="text"
              onPress={() => Linking.openURL('https://gravatar.com/profile')}
            >
              Change Photo
            </UniButton>
          </View>
        </View>
        <View style={formContainerStyle}>
          <FormFieldText
            control={control}
            disabled={isLoading}
            errors={errors}
            fieldName="firstName"
            isLoading={isLoading}
            label="First Name"
            required={true}
            style={{ flex: 2 }}
          />
          <FormFieldText
            control={control}
            disabled={isLoading}
            errors={errors}
            fieldName="lastName"
            isLoading={isLoading}
            label="Last Name"
            required={true}
            style={{ flex: 2 }}
          />
        </View>
        <UniButton
          disabled={isSubmitting || !isDirty}
          mode={buttonMode}
          onPress={handleSubmit(onSubmit)}
          style={buttonStyle}
        >
          Save
        </UniButton>
      </View>
    </View>
  )
}

const formSchema = z.object({
  id: z.string().uuid(),
  firstName: z.string().min(1, { message: 'Required' }),
  lastName: z.string().min(1, { message: 'Required' }),
})

type FormData = z.infer<typeof formSchema>

const styles = StyleSheet.create((theme) => ({
  avatarContainer: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.tokens.spacing[4],
    paddingBottom: theme.tokens.spacing[4],
  },
  avatarTextContainer: {
    flex: 1,
  },
  gravatarText: {
    color: theme.colors.subtitle,
    marginBottom: theme.tokens.spacing[2],
  },
}))

export default UserProfile
